import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { DynamicSearchEventsRefinements } from '#/modules/search/events/components/Refinements/DynamicSearchEventsRefinements';
import { searchEventsRoutePathByLocale } from '#/modules/search/events/routing/searchEvents.route-paths';
import { searchEventsRouteReference } from '#/modules/search/routing/search.routes';
import { getSearchPageLinkRels } from '#/modules/search/utils/getSearchPageLinkRels';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SearchEventsPage } from './SearchEventsPage';

export const handle = {
  searchRefinements: <DynamicSearchEventsRefinements />,
} satisfies LayoutHandleElements;

export function loader({
  url,
  context,
}: HorizonAppRouteLoaderArgs<typeof searchEventsRouteReference>) {
  const { serverContext } = getServerRouterContext(context);

  return {
    handle: {
      urlByLocale: searchEventsRoutePathByLocale,
      darkBackground: true,
      defaultSearchType: 'EVENT',
      hideFooterDivider: true,
      searchFromPageType: 'SEARCH',
      showDesktopSearch: true,
      showSearchRefinements: true,
      isSearchPage: true,
    } satisfies LayoutHandleProps,

    linkRels: getSearchPageLinkRels({
      searchParams: url.searchParams,
      locale: serverContext.locale,
      routePathByLocale: searchEventsRoutePathByLocale,
      referenceRoute: searchEventsRouteReference,
    }),

    now: serverContext.now,
  };
}

export default function SearchEventsHorizonRoute({
  loaderData: { linkRels, now },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n, locale } = useI18n();

  const ogImage = (() => {
    switch (locale) {
      case I18nLocale.EN:
        return;
      case I18nLocale.ES:
        return;
      case I18nLocale.PT:
        return getStaticImageSrc('og-image-pt-events.jpg');
    }
  })();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: events search page title */
        i18n('Social-Impact & Nonprofit Events | Online & Local Opportunities')
      }
      description={
        /* TRANSLATORS: events search page title */
        i18n('Discover social-impact events near you or online')
      }
      ogImage={ogImage}
      linkRels={linkRels}
    >
      <SearchEventsPage now={now} />
    </RouteModule>
  );
}
